import { useEffect, useState, useCallback, useRef } from "react";
import { Bell, FolderOpen, Users as UsersIcon, BellOff, BellRing, PenLine } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { pushStatus, subscribePush, unsubscribePush, pushSupported } from "@/lib/pushClient";

/**
 * Header bell – polls /api/messages/unread-counts every 30s and shows a badge.
 * Click opens a small dropdown listing project-/user-threads with unread items.
 */
export function NotificationBell() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ project: {}, user: {}, total: 0 });
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const [projectIdx, setProjectIdx] = useState({}); // pid -> {project_number, customer_name}
  const [userIdx, setUserIdx] = useState({}); // uid -> "first last"
  const [push, setPush] = useState({ supported: false, subscribed: false, permission: "default" });
  const [pushBusy, setPushBusy] = useState(false);
  const dropRef = useRef(null);

  const refreshPushStatus = useCallback(async () => {
    if (!pushSupported()) {
      setPush({ supported: false, subscribed: false, permission: "default" });
      return;
    }
    const s = await pushStatus();
    setPush(s);
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (push.subscribed) {
        await unsubscribePush();
        toast.success("Push-Benachrichtigungen deaktiviert");
      } else {
        await subscribePush();
        toast.success("Push aktiviert – du erhältst jetzt Benachrichtigungen");
      }
      await refreshPushStatus();
    } catch (e) {
      toast.error(e.message || "Push konnte nicht aktiviert werden");
    } finally {
      setPushBusy(false);
    }
  };

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get("/messages/unread-counts");
      setCounts(r.data);
    } catch { /* ignore */ }
    try {
      const n = await api.get("/notifications");
      setNotifs(n.data);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    refresh();
    refreshPushStatus();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [user, refresh, location.pathname, refreshPushStatus]);

  // When dropdown opens, load names lazily
  useEffect(() => {
    if (!open) return;
    const pids = Object.keys(counts.project || {});
    const uids = Object.keys(counts.user || {});
    if (pids.length) {
      api.get("/projects").then((r) => {
        const map = {};
        for (const p of r.data) map[p.id] = `${p.project_number || ""} · ${p.customer_name || ""}`;
        setProjectIdx(map);
      }).catch(() => {});
    }
    if (uids.length) {
      api.get("/users").then((r) => {
        const map = {};
        for (const u of r.data) map[u.id] = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.phone;
        setUserIdx(map);
      }).catch(() => {});
    }
  }, [open, counts]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!user) return null;

  const total = (counts.total || 0) + (notifs.unread || 0);

  const openNotif = async (n) => {
    setOpen(false);
    if (!n.read_at) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
      refresh();
    }
    if (n.url) navigate(n.url);
  };

  return (
    <div className="relative" ref={dropRef}>
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative h-11 w-11 rounded-xl bg-zinc-800/60 hover:bg-zinc-800 flex items-center justify-center transition active:scale-95"
        aria-label="Nachrichten"
        title={total > 0 ? `${total} ungelesene Nachricht(en)` : "Nachrichten"}
      >
        <Bell size={18} className={total > 0 ? "text-[#00E575]" : "text-zinc-300"} />
        {total > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#00E575] text-[#09090B] text-[12px] font-bold flex items-center justify-center shadow"
          >
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto bg-[#0E0E12] border border-zinc-800 rounded-2xl shadow-2xl z-50"
        >
          <div className="px-4 py-3 border-b border-zinc-800">
            <div className="font-display font-semibold">Ungelesene Nachrichten</div>
            <div className="text-xs text-zinc-300 mt-0.5">{total} ungelesen insgesamt</div>
          </div>

          {/* Push toggle */}
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-xs font-bold uppercase tracking-wider text-zinc-300">Push-Benachrichtigungen</div>
              <div className="text-[12px] text-zinc-300 mt-0.5">
                {!push.supported
                  ? "Auf diesem Gerät nicht verfügbar (installiere die App vom Home-Bildschirm)"
                  : push.permission === "denied"
                  ? "Im Browser blockiert – bitte in den Einstellungen erlauben"
                  : push.subscribed
                  ? "Aktiv – auch auf Sperrbildschirm"
                  : "Empfange Nachrichten auch bei geschlossener App"}
              </div>
            </div>
            <button
              data-testid="push-toggle-btn"
              onClick={togglePush}
              disabled={pushBusy || !push.supported || push.permission === "denied"}
              className={`h-9 px-3 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 flex-shrink-0 transition border ${
                push.subscribed
                  ? "bg-[#00E575]/15 border-[#00E575]/40 text-[#00E575] hover:bg-[#00E575]/25"
                  : "bg-zinc-800 border-zinc-700 text-zinc-200 hover:border-zinc-600"
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {push.subscribed ? <><BellRing size={13} /> Ein</> : <><BellOff size={13} /> Aus</>}
            </button>
          </div>

          {notifs.items.length > 0 && (
            <div className="py-2 border-b border-zinc-800" data-testid="notif-alerts-section">
              <div className="px-4 py-1.5 text-[12px] uppercase tracking-widest text-zinc-300 font-bold flex items-center gap-1.5">
                <PenLine size={11} /> Unterschriften
              </div>
              {notifs.items.slice(0, 8).map((n) => (
                <button
                  key={n.id}
                  onClick={() => openNotif(n)}
                  data-testid={`notif-alert-${n.id}`}
                  className={`w-full text-left px-4 py-2.5 hover:bg-zinc-900/60 flex gap-2 ${n.read_at ? "opacity-60" : ""}`}
                >
                  {!n.read_at && <span className="mt-1.5 h-2 w-2 rounded-full bg-[#00E575] shrink-0" />}
                  <span className="min-w-0">
                    <span className="block text-sm text-zinc-100 font-medium truncate">{n.title}</span>
                    <span className="block text-xs text-zinc-400">{n.body}</span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {(counts.total || 0) === 0 ? (
            notifs.items.length > 0 ? null : <div className="px-4 py-8 text-center text-sm text-zinc-400 font-medium">
              Keine ungelesenen Nachrichten.
            </div>
          ) : (
            <div className="py-2">
              {Object.keys(counts.project || {}).length > 0 && (
                <>
                  <div className="px-4 py-1.5 text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold flex items-center gap-1.5">
                    <FolderOpen size={11} /> Projekte
                  </div>
                  {Object.entries(counts.project).map(([pid, n]) => (
                    <Link
                      key={pid}
                      to={`/projects/${pid}?tab=messages`}
                      onClick={() => setOpen(false)}
                      data-testid={`notif-project-${pid}`}
                      className="flex items-center justify-between px-4 py-2.5 hover:bg-zinc-900/60"
                    >
                      <span className="text-sm text-zinc-200 truncate flex-1 mr-2">{projectIdx[pid] || pid.slice(0, 8)}</span>
                      <span className="px-2 py-0.5 rounded-full bg-[#00E575] text-[#09090B] text-[12px] font-bold">{n}</span>
                    </Link>
                  ))}
                </>
              )}
              {Object.keys(counts.user || {}).length > 0 && (
                <>
                  <div className="px-4 py-1.5 mt-2 text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold flex items-center gap-1.5">
                    <UsersIcon size={11} /> Personen
                  </div>
                  {Object.entries(counts.user).map(([uid, n]) => (
                    <Link
                      key={uid}
                      to={user.role === "sales_rep" ? `/messages/user/${uid}` : `/manager/team/${uid}`}
                      onClick={() => setOpen(false)}
                      data-testid={`notif-user-${uid}`}
                      className="flex items-center justify-between px-4 py-2.5 hover:bg-zinc-900/60"
                    >
                      <span className="text-sm text-zinc-200 truncate flex-1 mr-2">{userIdx[uid] || uid.slice(0, 8)}</span>
                      <span className="px-2 py-0.5 rounded-full bg-[#00E575] text-[#09090B] text-[12px] font-bold">{n}</span>
                    </Link>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
